/**
 * Unified "Partner Hub" card — one card with a Creator / Business toggle.
 * Creator state uses the sunset creator palette; Business uses emerald→sky.
 * Status-aware: verified creators / partner owners see their hub CTA.
 */
import { useEffect, useState } from "react";
import { ArrowRight, BadgeCheck, Building2, FileText, MapPin, Percent, Sparkles, Stamp, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useCreatorStatus } from "@/hooks/useCreatorStatus";
import { useMyPartner } from "@/hooks/usePartnerPlaces";

type Mode = "creator" | "business";
const TAB_KEY = "goaldigger.partner_hub_tab";

export function AudienceEntryTiles() {
  const creator = useCreatorStatus();
  const { data: partner, isLoading: partnerLoading } = useMyPartner();
  const [mode, setModeState] = useState<Mode | null>(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(TAB_KEY) : null;
    return saved === "creator" || saved === "business" ? saved : null;
  });
  // Smart default once roles load: business owners land on Business, others on Creator.
  useEffect(() => {
    if (mode || creator.isLoading || partnerLoading) return;
    setModeState(partner ? "business" : "creator");
  }, [mode, creator.isLoading, partnerLoading, partner]);
  const setMode = (m: Mode) => {
    setModeState(m);
    try { localStorage.setItem(TAB_KEY, m); } catch { /* ignore */ }
  };

  if (creator.isLoading || partnerLoading || !mode) return null;

  const isCreator = creator.status !== "none";
  const isBiz = mode === "business";

  const content = isBiz
    ? {
        kicker: "For business owners",
        title: partner ? partner.brandName : "Put your place on the UAE map",
        copy: partner
          ? `Your venue is live on GoalDigger · ${partner.claimStatus}`
          : "Get discovered by explorers on trails, passports and creator stories.",
        perks: [
          { icon: MapPin, label: "Free map pin" },
          { icon: Percent, label: "0% commission" },
          { icon: Stamp, label: "Passport stamps" },
        ],
        cta: partner ? "Open business dashboard" : "List or claim — free",
        to: partner ? "/advertiser-dashboard" : "/list-your-business",
        secondary: { label: "See the map", to: "/explore" },
      }
    : {
        kicker: "For creators & influencers",
        title: isCreator ? "Your Creator Hub" : "Get credited for the places you post",
        copy: isCreator ? creator.statusLine : "Feature UAE places, join trails and earn rewards.",
        perks: [
          { icon: FileText, label: "Free invoices" },
          { icon: BadgeCheck, label: "Verified profile" },
          { icon: Sparkles, label: "Trail credits" },
        ],
        cta: isCreator ? "Open Creator Hub" : "Become a Creator — free",
        to: creator.to,
        secondary: { label: "Browse creators", to: "/creators" },
      };

  return (
    <section
      aria-label="Creators and businesses"
      className={cn(
        "relative isolate overflow-hidden rounded-2xl border border-border/60 bg-card p-4 shadow-md sm:p-5 transition-colors"
      )}
    >
      {/* Brand wash */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 -z-10 opacity-[0.12] transition-opacity",
          isBiz ? "business-gradient" : "creator-gradient"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -right-10 -top-10 -z-10 h-40 w-40 rounded-full blur-3xl opacity-40",
          isBiz ? "business-gradient" : "creator-gradient"
        )}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-foreground">Who are you?</p>
        <div role="tablist" aria-label="Choose who you are" className="inline-flex rounded-full border border-border/60 bg-background/80 p-1 backdrop-blur">
          {([
            { k: "creator", label: "Creator", icon: Sparkles },
            { k: "business", label: "Business", icon: Building2 },
          ] as const).map(({ k, label, icon: Icon }) => {
            const active = mode === k;
            return (
              <button
                key={k}
                role="tab"
                type="button"
                aria-selected={active}
                onClick={() => setMode(k)}
                style={{ touchAction: "manipulation" }}
                className={cn(
                  "inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3.5 text-xs font-bold transition",
                  active
                    ? cn("text-primary-foreground shadow-md", k === "business" ? "business-gradient" : "creator-gradient")
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
              </button>
            );
          })}
        </div>
      </div>

      <div key={mode} className="mt-4 animate-fade-in">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-primary-foreground shadow-lg",
              isBiz ? "business-gradient" : "creator-gradient"
            )}
          >
            {isBiz ? <Building2 className="h-6 w-6" aria-hidden /> : <Sparkles className="h-6 w-6" aria-hidden />}
          </span>
          <div className="min-w-0">
            <p className={cn("text-[11px] font-bold uppercase tracking-wider", isBiz ? "business-text" : "creator-text")}>
              {content.kicker}
            </p>
            <h3 className="text-lg font-extrabold leading-tight text-foreground">{content.title}</h3>
            <p className="mt-1 text-sm leading-snug text-muted-foreground">{content.copy}</p>
          </div>
        </div>

        <ul className="mt-4 grid grid-cols-3 gap-2">
          {content.perks.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="flex flex-col items-center gap-1 rounded-xl border border-border/60 bg-background/70 px-2 py-2.5 text-center"
            >
              <Icon className={cn("h-4 w-4", isBiz ? "business-text" : "creator-text")} aria-hidden />
              <span className="text-[11px] font-semibold leading-tight text-foreground">{label}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Link
            to={content.to}
            className={cn(
              "group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold text-primary-foreground shadow-md transition hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.98]",
              isBiz ? "business-gradient" : "creator-gradient"
            )}
          >
            {content.cta}
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
          </Link>
          <Link
            to={content.secondary.to}
            className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl px-4 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            {isBiz ? <MapPin className="h-4 w-4" aria-hidden /> : <Users className="h-4 w-4" aria-hidden />}
            {content.secondary.label}
          </Link>
        </div>
      </div>
    </section>
  );
}
